import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAccount, useSignMessage, useDisconnect, useConnect } from 'wagmi';

// Storage keys
const AUTH_STORAGE_KEY = 'mfersarcade_auth';
const PENDING_WALLET_SIGN_KEY = 'mfersarcade_pending_wallet_sign';

// Token expiry buffer (refresh 1 hour before expiry)
const EXPIRY_BUFFER_MS = 60 * 60 * 1000;

/**
 * Stored auth data structure
 */
interface StoredAuth {
  token: string;
  userId: string;
  displayName: string;
  authType: 'wallet' | 'twitter';
  walletAddress: string | null;
  twitterHandle: string | null;
  profileImageUrl: string | null;
  expiresAt: number;
}

/**
 * Auth user returned by the hook
 */
export interface AuthUser {
  id: string;
  displayName: string;
  authType: 'wallet' | 'twitter';
  walletAddress: string | null;
  twitterHandle: string | null;
  profileImageUrl: string | null;
}

/**
 * Auth headers for API requests
 */
export interface AuthHeaders {
  Authorization: string;
}

/**
 * Available wallet types
 */
export type WalletType = 'metamask' | 'coinbase' | 'phantom';

/**
 * Return type of useAuth hook
 */
export interface UseAuthReturn {
  // State
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AuthUser | null;
  error: string | null;

  // Wallet auth
  isWalletConnected: boolean;
  isSigningMessage: boolean;
  isConnecting: boolean;
  availableWallets: { id: WalletType; name: string; available: boolean }[];
  connectWallet: (walletType: WalletType) => void;
  signInWithWallet: () => Promise<void>;

  // Twitter auth
  signInWithTwitter: () => Promise<void>;

  // Common
  signOut: () => Promise<void>;
  getAuthHeaders: () => AuthHeaders | null;
  clearError: () => void;
  updateDisplayName: (newName: string) => void;
}

/**
 * Create the sign-in message for wallet auth
 */
function createSignInMessage(walletAddress: string): string {
  const timestamp = Math.floor(Date.now() / 1000);
  return `Welcome to MfersArcade!

Wallet: ${walletAddress}
Time: ${timestamp}

Sign this message to verify your wallet.`;
}

/**
 * Load auth from localStorage
 */
function loadStoredAuth(): StoredAuth | null {
  try {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!stored) return null;

    const auth = JSON.parse(stored) as StoredAuth;

    // Check if expired (with buffer)
    if (auth.expiresAt < Date.now() + EXPIRY_BUFFER_MS) {
      localStorage.removeItem(AUTH_STORAGE_KEY);
      return null;
    }

    return auth;
  } catch {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    return null;
  }
}

/**
 * Save auth to localStorage
 */
function saveAuth(auth: StoredAuth): void {
  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(auth));
  // Dispatch event for cross-tab sync
  window.dispatchEvent(new CustomEvent('auth-storage-update'));
}

/**
 * Clear auth from localStorage
 */
function clearAuth(): void {
  localStorage.removeItem(AUTH_STORAGE_KEY);
  window.dispatchEvent(new CustomEvent('auth-storage-update'));
}

/**
 * Clear all wallet-related data from localStorage
 */
function clearWalletStorage(): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.startsWith('walletlink') ||
        key.startsWith('-walletlink') ||
        key.startsWith('wagmi') ||
        key.startsWith('wc@') ||
        key.startsWith('WALLETCONNECT')
      )) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    console.log('Cleared wallet storage keys:', keysToRemove.length);
  } catch (e) {
    console.log('Error clearing wallet storage:', e);
  }
}

/**
 * Main authentication hook
 */
export function useAuth(): UseAuthReturn {
  // Local state
  const [storedAuth, setStoredAuth] = useState<StoredAuth | null>(() => loadStoredAuth());
  const [isLoading, setIsLoading] = useState(false);
  const [isSigningMessage, setIsSigningMessage] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Wagmi hooks
  const { address: walletAddress, isConnected: isWalletConnected } = useAccount();
  const { signMessageAsync } = useSignMessage();
  const { disconnect } = useDisconnect();
  const { connect, connectors, isPending: isConnecting } = useConnect();

  // Derived state
  const isAuthenticated = storedAuth !== null;
  const user: AuthUser | null = useMemo(() => {
    if (!storedAuth) return null;
    return {
      id: storedAuth.userId,
      displayName: storedAuth.displayName,
      authType: storedAuth.authType,
      walletAddress: storedAuth.walletAddress,
      twitterHandle: storedAuth.twitterHandle,
      profileImageUrl: storedAuth.profileImageUrl,
    };
  }, [storedAuth]);

  // Map connectors to wallet types
  const availableWallets = useMemo(() => {
    const hasMetaMask = typeof window !== 'undefined' && window.ethereum?.isMetaMask;
    const hasCoinbase = typeof window !== 'undefined' && (window.ethereum?.isCoinbaseWallet || window.ethereum?.providers?.some((p: { isCoinbaseWallet?: boolean }) => p.isCoinbaseWallet));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const hasPhantom = typeof window !== 'undefined' && (window as any).phantom?.ethereum?.isPhantom;

    return [
      {
        id: 'metamask' as WalletType,
        name: 'MetaMask',
        available: hasMetaMask || false,
      },
      {
        id: 'coinbase' as WalletType,
        name: 'Coinbase Wallet',
        available: hasCoinbase || true, // Coinbase can always be used via mobile link
      },
      {
        id: 'phantom' as WalletType,
        name: 'Phantom',
        available: hasPhantom || false,
      },
    ];
  }, []);

  // Listen for storage changes (cross-tab sync)
  useEffect(() => {
    const handleStorageChange = () => {
      setStoredAuth(loadStoredAuth());
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('auth-storage-update', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('auth-storage-update', handleStorageChange);
    };
  }, []);

  // Check token expiry periodically
  useEffect(() => {
    if (!storedAuth) return;

    const checkExpiry = () => {
      if (storedAuth.expiresAt < Date.now() + EXPIRY_BUFFER_MS) {
        clearAuth();
        setStoredAuth(null);
      }
    };

    const interval = setInterval(checkExpiry, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [storedAuth]);

  // Track if we initiated a wallet connection (to auto-sign after connect)
  // Use sessionStorage to persist across page refreshes during wallet flow
  const [pendingWalletSign, setPendingWalletSignState] = useState(() => {
    try {
      return sessionStorage.getItem(PENDING_WALLET_SIGN_KEY) === 'true';
    } catch {
      return false;
    }
  });

  // Wrapper to also update sessionStorage
  const setPendingWalletSign = useCallback((value: boolean) => {
    setPendingWalletSignState(value);
    try {
      if (value) {
        sessionStorage.setItem(PENDING_WALLET_SIGN_KEY, 'true');
      } else {
        sessionStorage.removeItem(PENDING_WALLET_SIGN_KEY);
      }
    } catch {
      // Ignore storage errors
    }
  }, []);

  /**
   * Sign in with connected wallet
   */
  const signInWithWallet = useCallback(async () => {
    if (!walletAddress) {
      setError('Wallet not connected');
      return;
    }

    setIsSigningMessage(true);
    setError(null);

    try {
      // Create and sign message
      const message = createSignInMessage(walletAddress);
      const signature = await signMessageAsync({
        message,
        account: walletAddress as `0x${string}`,
      });

      // Verify with backend
      const response = await fetch('/api/auth/wallet/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          wallet_address: walletAddress,
          message,
          signature,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Verification failed');
      }

      if (data.status === 'existing_user') {
        // Existing user - save token and done
        const auth: StoredAuth = {
          token: data.token,
          userId: data.user.id,
          displayName: data.user.display_name,
          authType: 'wallet',
          walletAddress: data.user.wallet_address,
          twitterHandle: null,
          profileImageUrl: null,
          expiresAt: Date.now() + (data.expires_in * 1000),
        };
        saveAuth(auth);
        setStoredAuth(auth);
      } else if (data.status === 'new_user') {
        // New user - redirect to registration
        const params = new URLSearchParams({
          temp_token: data.temp_token,
          auth_type: 'wallet',
          identifier: data.identifier,
          suggested_name: data.suggested_name,
        });
        window.location.href = `/register?${params.toString()}`;
      }

    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign in failed';
      setError(message);
      console.error('Wallet sign in error:', err);
    } finally {
      setIsSigningMessage(false);
    }
  }, [walletAddress, signMessageAsync]);

  /**
   * Connect to a specific wallet and auto-sign after
   */
  const connectWallet = useCallback(async (walletType: WalletType) => {
    console.log('connectWallet called:', walletType);
    console.log('Available connectors:', connectors.map(c => ({ id: c.id, name: c.name })));
    console.log('Current wallet state:', { isWalletConnected, walletAddress });

    setError(null);

    // Check if wallet extension is available before attempting to connect
    const walletInfo = availableWallets.find(w => w.id === walletType);
    if (walletType === 'metamask' && !walletInfo?.available) {
      setError('MetaMask not detected. Please install the MetaMask browser extension.');
      return;
    }
    if (walletType === 'phantom' && !walletInfo?.available) {
      setError('Phantom not detected. Please install the Phantom browser extension.');
      return;
    }

    // Find the right connector by ID
    const connector = connectors.find(c => {
      if (walletType === 'metamask') {
        // injected with target: 'metaMask' has id 'injected'
        return c.id === 'io.metamask' || c.id === 'metaMask' || c.id === 'injected';
      }
      if (walletType === 'coinbase') {
        return c.id === 'coinbaseWalletSDK' || c.id === 'coinbaseWallet';
      }
      if (walletType === 'phantom') {
        return c.id === 'phantom';
      }
      return false;
    });

    console.log('Selected connector:', connector ? { id: connector.id, name: connector.name } : 'none');

    if (!connector) {
      setError(`${walletType} wallet not available`);
      return;
    }

    // If a wallet is already connected, disconnect first to switch wallets
    if (isWalletConnected) {
      console.log('Disconnecting current wallet before connecting new one...');
      disconnect();
      clearWalletStorage();
      // Small delay to ensure disconnect completes
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    setPendingWalletSign(true); // Will auto-trigger signInWithWallet after connect

    // Wrap connect in try-catch to handle ProviderNotFoundError
    try {
      connect({ connector }, {
        onError: (err) => {
          console.error('Wallet connect error:', err);
          setPendingWalletSign(false);

          // Handle ProviderNotFoundError specifically (check message since type doesn't include it)
          const errorMessage = err.message || '';
          const isProviderNotFound = errorMessage.includes('Provider not found') ||
                                     errorMessage.includes('ProviderNotFoundError');

          if (isProviderNotFound) {
            const walletName = walletType === 'metamask' ? 'MetaMask' :
                              walletType === 'coinbase' ? 'Coinbase Wallet' :
                              walletType === 'phantom' ? 'Phantom' : 'Wallet';
            setError(`${walletName} not detected. Please install the browser extension or use a wallet app.`);
          } else {
            setError(errorMessage || 'Failed to connect wallet');
          }
        }
      });
    } catch (err) {
      console.error('Wallet connect exception:', err);
      setPendingWalletSign(false);

      // Handle ProviderNotFoundError
      const errorMessage = err instanceof Error ? err.message : '';
      const isProviderNotFound = errorMessage.includes('Provider not found') ||
                                 errorMessage.includes('ProviderNotFoundError');

      if (isProviderNotFound) {
        const walletName = walletType === 'metamask' ? 'MetaMask' :
                          walletType === 'coinbase' ? 'Coinbase Wallet' :
                          walletType === 'phantom' ? 'Phantom' : 'Wallet';
        setError(`${walletName} not detected. Please install the browser extension or use a wallet app.`);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to connect wallet');
      }
    }
  }, [connect, connectors, isWalletConnected, walletAddress, disconnect, setPendingWalletSign, availableWallets]);

  // Auto-trigger sign-in after wallet connects (when user initiated connection)
  useEffect(() => {
    console.log('Wallet state:', {
      pendingWalletSign,
      isWalletConnected,
      walletAddress,
      isAuthenticated,
      isSigningMessage,
    });

    if (pendingWalletSign && isWalletConnected && walletAddress && !isAuthenticated && !isSigningMessage) {
      console.log('Auto-triggering signInWithWallet...');
      // Small delay to ensure wallet is fully ready
      const timer = setTimeout(() => {
        // Clear pending flag right before signing to avoid race with stale cleanup
        setPendingWalletSign(false);
        signInWithWallet();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [pendingWalletSign, isWalletConnected, walletAddress, isAuthenticated, isSigningMessage, signInWithWallet, setPendingWalletSign]);

  // Clean up stale wallet connections - if wallet is connected but user isn't authenticated
  // and there's no pending sign-in, disconnect to reset to clean state
  useEffect(() => {
    if (isWalletConnected && !isAuthenticated && !pendingWalletSign && !isSigningMessage) {
      console.log('Cleaning up stale wallet connection...');
      // Small delay to avoid disconnecting during initial auth flow
      const timer = setTimeout(() => {
        // Re-check conditions after delay
        if (!pendingWalletSign && !isSigningMessage) {
          disconnect();
          clearWalletStorage();
        }
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isWalletConnected, isAuthenticated, pendingWalletSign, isSigningMessage, disconnect]);

  /**
   * Start Twitter OAuth flow
   */
  const signInWithTwitter = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Check if Twitter auth is enabled (OAuth 2.0)
      const statusRes = await fetch('/api/auth/twitter2/status');
      const statusData = await statusRes.json();

      if (!statusData.enabled) {
        throw new Error('Twitter sign-in is not available');
      }

      // Get authorization URL (OAuth 2.0 with PKCE)
      const response = await fetch('/api/auth/twitter2/request', {
        method: 'POST',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to start Twitter auth');
      }

      // Redirect to Twitter
      window.location.href = data.authorize_url;

    } catch (err) {
      const message = err instanceof Error ? err.message : 'Twitter sign in failed';
      setError(message);
      setIsLoading(false);
      console.error('Twitter sign in error:', err);
    }
  }, []);

  /**
   * Sign out
   */
  const signOut = useCallback(async () => {
    // Clear stored auth
    clearAuth();
    setStoredAuth(null);

    // Disconnect wallet if connected
    if (isWalletConnected) {
      disconnect();
    }

    // Clear all wallet-related localStorage
    clearWalletStorage();
  }, [isWalletConnected, disconnect]);

  /**
   * Get auth headers for API requests
   */
  const getAuthHeaders = useCallback((): AuthHeaders | null => {
    if (!storedAuth) return null;
    return {
      Authorization: `Bearer ${storedAuth.token}`,
    };
  }, [storedAuth]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Update display name in stored auth (after server update)
   */
  const updateDisplayName = useCallback((newName: string) => {
    if (!storedAuth) return;
    const updatedAuth = { ...storedAuth, displayName: newName };
    saveAuth(updatedAuth);
    setStoredAuth(updatedAuth);
  }, [storedAuth]);

  return {
    // State
    isAuthenticated,
    isLoading,
    user,
    error,

    // Wallet
    isWalletConnected,
    isSigningMessage,
    isConnecting,
    availableWallets,
    connectWallet,
    signInWithWallet,

    // Twitter
    signInWithTwitter,

    // Common
    signOut,
    getAuthHeaders,
    clearError,
    updateDisplayName,
  };
}

export default useAuth;
